// solution-pieces-merge (Trello daAruJ5U) — "Show the solution for this step" on a step-by-step
// challenge: the reached step's solution as small pieces, each merged as whole lines BELOW the
// student's cursor. The student's own code is never swapped.
//
// Imported by challenge-chrome.js only (the step index lives in its `renderVisit` / `goTo`
// closure), never wired from main.js. editor-core.js / editor-api.js are NOT edited: the merge is
// ONE ordinary, undoable Ace insert through editor-core's public `core.getEditor()`.
//
// Exports:
//   * `insertBelowCursor(text, row, block)` — the pure rule the live merge follows.
//   * `mergeBlock(editor, block)` — the live Ace operation (one `session.insert`).
//   * `fetchPieces(fetchFn, courseCtx, parent)` — the POST to `…/challengeSolutionPieces`.
//   * `createPiecesPanel(opts)` — the step-panel UI (`mount(stepEl, { step, reached })`,
//     `refresh()`), mounted by challenge-chrome after the step's hint row.
//
// Known limit (decisions.md): every honoured step's pieces travel in the one reply; the
// failed-Check counter and the reached-step rule are a process gate, not security.

import { markSolutionPieceInsert, clearSolutionPieceInsert } from './authenticity-capture.js';

export var PIECES_COPY = {
  stepLink: 'Show the solution for this step',
  loading: 'Loading the solution…',
  loadFailed: 'Couldn\'t load the solution. Try again.',
  retry: 'Try again',
  merge: 'Merge at cursor',
  merged: 'Merged',
  whole: 'Show me the whole solution'
};

export function wrongTabCopy(file) { return 'Open the ' + file + ' tab first'; }
export function mergedCopy(insertedAtRow, wasEmpty) {
  return wasEmpty ? 'Merged at line 1' : 'Merged below line ' + insertedAtRow;
}

// CRLF / CR to LF, then trailing newlines stripped (the normaliser's rule).
export function normaliseBlock(block) {
  return String(block == null ? '' : block).replace(/\r\n?/g, '\n').replace(/\n+$/, '');
}

/**
 * Insert `block`'s lines as whole new lines BELOW line `row` (0-based; that line is untouched).
 * The block's indentation is kept verbatim, a trailing newline in it is stripped, the document's
 * own line ending is preserved, and an empty document simply becomes the block.
 * Returns `{ text, insertedAtRow, caretRow, caretColumn }` (rows 0-based; the caret sits at the
 * end of the inserted block).
 */
export function insertBelowCursor(text, row, block) {
  var doc = String(text == null ? '' : text);
  var blockLines = normaliseBlock(block).split('\n');
  var last = blockLines[blockLines.length - 1];
  var eol = doc.indexOf('\r\n') >= 0 ? '\r\n' : '\n';
  if (doc === '') {
    return { text: blockLines.join(eol), insertedAtRow: 0, caretRow: blockLines.length - 1, caretColumn: last.length };
  }
  var lines = doc.split(/\r\n|\n|\r/);
  var r = Math.max(0, Math.min(lines.length - 1, Number.isInteger(row) ? row : 0));
  var out = lines.slice(0, r + 1).concat(blockLines, lines.slice(r + 1));
  return {
    text: out.join(eol),
    insertedAtRow: r + 1,
    caretRow: r + blockLines.length,
    caretColumn: last.length
  };
}

function sessionOf(editor) {
  if (!editor) { return null; }
  if (typeof editor.getSession === 'function') { return editor.getSession(); }
  return editor.session || null;
}

/**
 * The live merge: ONE `session.insert({ row, column: lineLength }, '\n' + block)` at the
 * selection's LEAD row (an empty document gets the block itself, still one insert), caret to the
 * block's end, focus, and the merged lines highlighted until the student's next edit.
 * Returns `{ insertedAtRow, wasEmpty }` (insertedAtRow 1-based for the status line = the cursor
 * line), or null when nothing could be inserted.
 */
export function mergeBlock(editor, block) {
  var session = sessionOf(editor);
  if (!session || typeof session.insert !== 'function') { return null; }
  var normal = normaliseBlock(block);
  var value = typeof session.getValue === 'function' ? session.getValue() : '';
  var wasEmpty = value === '';
  var length = typeof session.getLength === 'function' ? session.getLength() : 1;
  var pos = typeof editor.getCursorPosition === 'function' ? editor.getCursorPosition() : null;
  var row = Math.max(0, Math.min(length - 1, pos && Number.isInteger(pos.row) ? pos.row : 0));
  var plan = insertBelowCursor(wasEmpty ? '' : value, row, normal);
  // The authenticity capture (where wired) must not record this insert as a paste / large insert
  // or count it as typed; the one-shot flag is consumed synchronously by Ace's change event, and
  // is cleared in `finally` whatever happens.
  markSolutionPieceInsert();
  try {
    if (wasEmpty) {
      session.insert({ row: 0, column: 0 }, normal);
    } else {
      var line = typeof session.getLine === 'function' ? String(session.getLine(row) || '') : '';
      session.insert({ row: row, column: line.length }, '\n' + normal);
    }
  } finally {
    clearSolutionPieceInsert();
  }
  if (typeof editor.clearSelection === 'function') { editor.clearSelection(); }
  if (typeof editor.moveCursorTo === 'function') { editor.moveCursorTo(plan.caretRow, plan.caretColumn); }
  if (typeof editor.focus === 'function') { editor.focus(); }
  highlightUntilNextEdit(editor, session, plan.insertedAtRow, plan.caretRow);
  return { insertedAtRow: wasEmpty ? 1 : row + 1, wasEmpty: wasEmpty };
}

// Ace's `highlightLines` builds its own Range (no `ace` global needed). Registered AFTER the
// insert returns, because Ace fires `change` synchronously inside `session.insert`.
function highlightUntilNextEdit(editor, session, startRow, endRow) {
  if (typeof session.highlightLines !== 'function' || typeof editor.on !== 'function') { return; }
  var marker = null;
  try { marker = session.highlightLines(startRow, endRow, 'cp-merged-line', false); } catch (e) { marker = null; }
  if (!marker || marker.id == null) { return; }
  function clear() {
    if (typeof session.removeMarker === 'function') { session.removeMarker(marker.id); }
    if (typeof editor.off === 'function') { editor.off('change', clear); }
    else if (typeof editor.removeListener === 'function') { editor.removeListener('change', clear); }
  }
  editor.on('change', clear);
}

export function piecesUrl(courseCtx) {
  var c = courseCtx || {};
  return '/api/courses/' + encodeURIComponent(c.courseId || '') +
    '/lessons/' + encodeURIComponent(c.lessonId || '') +
    '/materials/' + encodeURIComponent(c.materialId || '') +
    '/challengeSolutionPieces';
}

function cleanPieces(list) {
  if (!Array.isArray(list)) { return null; }
  return list.filter(function (p) { return p && typeof p.code === 'string' && Number.isInteger(Number(p.step)); })
    .map(function (p) {
      return {
        step: Number(p.step),
        label: p.label == null ? '' : String(p.label),
        where: p.where == null ? '' : String(p.where),
        file: p.file ? String(p.file) : 'main.py',
        code: p.code
      };
    });
}

/**
 * POST `{ parent }`. Resolves `{ pieces }`. Rejects with `refused: true` on a 400 (pieces no
 * longer honoured — branch on the STATUS, never the message); any other failure, or a 200 with
 * no `pieces` array (a Joi failure answers a silent 200), rejects without it.
 */
export function fetchPieces(fetchFn, courseCtx, parent) {
  if (typeof fetchFn !== 'function') { return Promise.reject(new Error('challenge-pieces: no fetch')); }
  return Promise.resolve(fetchFn(piecesUrl(courseCtx), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ parent: parent })
  })).then(function (res) {
    if (!res || !res.ok) {
      var err = new Error('challenge-pieces: request failed (' + (res && res.status) + ')');
      err.refused = !!res && res.status === 400;
      throw err;
    }
    var parsed = typeof res.json === 'function' ? Promise.resolve(res.json()) : Promise.resolve(null);
    return parsed.then(function (data) {
      var pieces = cleanPieces(data && data.pieces);
      if (!pieces) { throw new Error('challenge-pieces: no pieces in the reply'); }
      return { pieces: pieces };
    });
  });
}

function defaultFetch() {
  return (typeof globalThis !== 'undefined' && typeof globalThis.fetch === 'function')
    ? globalThis.fetch.bind(globalThis) : null;
}

/**
 * The step-panel UI. opts: { doc, core, solution, pieceSteps, courseCtx, attemptId, fetch? }.
 * `solution` is challenge-solution.js's controller (isOffered / checking / locked / openView).
 * State that outlives a step visit (the fetched pieces, merged pieces, revealed steps) lives here,
 * never in the DOM. `mount` builds one `.cp-panel` per visit; `refresh` updates it in place
 * (never moves focus or scroll).
 */
export function createPiecesPanel(opts) {
  opts = opts || {};
  var doc = opts.doc;
  var core = opts.core;
  var solution = opts.solution;
  var steps = (Array.isArray(opts.pieceSteps) ? opts.pieceSteps : []).map(Number).filter(Number.isInteger);
  var fetchFn = opts.fetch || defaultFetch();

  var state = {
    load: 'idle', // idle | loading | loaded | failed | refused
    pieces: null,
    merged: {},   // reply index -> true (per page load; no re-detection)
    revealed: {}  // step -> true once its cards were shown
  };
  var view = null; // { container, body, status, step, reached, buttons: [{ btn, piece, index }] }

  function el(tag, cls, text) {
    var node = doc.createElement(tag);
    if (cls) { node.className = cls; }
    if (text != null) { node.textContent = text; }
    node.hidden = false;
    return node;
  }
  function button(cls, text) {
    var b = el('button', cls, text);
    b.type = 'button';
    b.setAttribute('type', 'button');
    return b;
  }
  function clear(node) {
    if (typeof node.replaceChildren === 'function') { node.replaceChildren(); } else { node.innerHTML = ''; }
  }

  function activeFile() {
    try {
      return (core && typeof core.getActiveFile === 'function' && core.getActiveFile()) || 'main.py';
    } catch (e) { return 'main.py'; }
  }
  function blocked() {
    return !!(solution && ((typeof solution.checking === 'function' && solution.checking()) ||
      (typeof solution.locked === 'function' && solution.locked())));
  }
  function editable() {
    if (!core || typeof core.getEditor !== 'function') { return null; }
    if (typeof core.isReadOnly === 'function' && core.isReadOnly()) { return null; }
    return core.getEditor() || null;
  }

  function applyButton(entry) {
    var btn = entry.btn;
    var piece = entry.piece;
    var card = btn.parentNode;
    if (state.merged[entry.index]) {
      btn.textContent = PIECES_COPY.merged;
      btn.disabled = true;
      if (card && card.classList) { card.classList.toggle('is-merged', true); }
      return;
    }
    if (piece.file !== activeFile()) {
      btn.textContent = wrongTabCopy(piece.file);
      btn.disabled = true;
      return;
    }
    btn.textContent = PIECES_COPY.merge;
    btn.disabled = blocked();
  }

  function merge(entry) {
    if (!view || state.merged[entry.index]) { return; }
    applyButton(entry);
    if (entry.btn.disabled) { return; }
    var editor = editable();
    if (!editor) { return; }
    var done = mergeBlock(editor, entry.piece.code);
    if (!done) { return; }
    state.merged[entry.index] = true;
    applyButton(entry);
    view.status.textContent = mergedCopy(done.insertedAtRow, done.wasEmpty);
  }

  function wholeLink() {
    var link = button('cp-link cp-whole', PIECES_COPY.whole);
    link.addEventListener('click', function () {
      if (solution && typeof solution.openView === 'function') { solution.openView(link); }
    });
    return link;
  }

  function reveal(step) {
    if (state.load === 'loading') { return; }
    if (state.load === 'loaded') { state.revealed[step] = true; renderBody(); return; }
    state.load = 'loading';
    renderBody();
    fetchPieces(fetchFn, opts.courseCtx, opts.attemptId).then(function (res) {
      state.pieces = res.pieces;
      state.load = 'loaded';
      state.revealed[step] = true;
      renderBody();
    }, function (err) {
      state.load = (err && err.refused) ? 'refused' : 'failed';
      renderBody();
    });
  }

  function renderCards(body, step) {
    view.buttons = [];
    (state.pieces || []).forEach(function (piece, index) {
      if (piece.step !== step) { return; }
      var card = el('div', 'cp-card');
      card.appendChild(el('p', 'cp-label', piece.label));
      if (piece.where) { card.appendChild(el('p', 'cp-where', piece.where)); }
      var pre = el('pre', 'cp-code');
      pre.appendChild(el('code', null, normaliseBlock(piece.code)));
      card.appendChild(pre);
      var btn = button('cp-merge', PIECES_COPY.merge);
      card.appendChild(btn);
      var entry = { btn: btn, piece: piece, index: index };
      btn.addEventListener('click', function () { merge(entry); });
      body.appendChild(card);
      view.buttons.push(entry);
      applyButton(entry);
    });
  }

  function renderBody() {
    if (!view) { return; }
    var body = view.body;
    clear(body);
    view.buttons = [];
    var offered = !!(solution && typeof solution.isOffered === 'function' && solution.isOffered());
    var show = offered && view.reached;
    view.container.hidden = !show;
    if (!show) { return; }
    var step = view.step;
    if (steps.indexOf(step) < 0 || state.load === 'refused') {
      body.appendChild(wholeLink());
      return;
    }
    if (state.load === 'loaded' && state.revealed[step]) {
      renderCards(body, step);
      body.appendChild(wholeLink());
      return;
    }
    var failed = state.load === 'failed';
    var loading = state.load === 'loading';
    var link = button('cp-link cp-step-link', loading ? PIECES_COPY.loading : (failed ? PIECES_COPY.retry : PIECES_COPY.stepLink));
    link.disabled = loading;
    if (loading) { link.setAttribute('aria-busy', 'true'); }
    link.addEventListener('click', function () { reveal(step); });
    if (failed) {
      var errLine = el('p', 'cp-error', PIECES_COPY.loadFailed);
      errLine.setAttribute('role', 'alert');
      body.appendChild(errLine);
    }
    body.appendChild(link);
  }

  return {
    // One step visit: append a `.cp-panel` to the step body (hidden when nothing applies).
    // `step` is 1-based; `reached` = index <= the first unticked step (all ticked = every step).
    mount: function (stepEl, ctx) {
      if (!doc || !stepEl) { view = null; return null; }
      var container = el('div', 'cp-panel');
      var body = el('div', 'cp-body');
      var status = el('p', 'cp-status');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      container.appendChild(body);
      container.appendChild(status);
      stepEl.appendChild(container);
      view = { container: container, body: body, status: status, step: ctx && ctx.step,
        reached: !!(ctx && ctx.reached), buttons: [] };
      renderBody();
      return container;
    },
    // In place: the offer crossed the threshold (rebuild) or a Check / tab switch changed a
    // button's state (buttons only, so focus is kept).
    refresh: function (full) {
      if (!view) { return; }
      var offered = !!(solution && typeof solution.isOffered === 'function' && solution.isOffered());
      if (full || view.container.hidden === (offered && view.reached)) { renderBody(); return; }
      view.buttons.forEach(applyButton);
    },
    // Test seam.
    _state: state
  };
}
